import { Injectable } from '@angular/core';
import chameleon from '@chamaeleonidae/chmln';
import * as fromRoot from '../../reducers';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { PermissionActions, Role, User } from '../models';
import { SkuService } from './sku.service';
import { UserService } from 'src/app/core/user.service';
import { MultiAdminService } from 'src/app/core/multiadmin.service';
import { BlendService } from './blend.service';
import { isUrlHistoryClean } from 'src/app/util/blend-helper';
import { ChargebeeService } from './chargebee.service';

@Injectable({
  providedIn: 'root'
})
export class ChameleonService {
  private userSubscribe: Subscription;
  private chargebeeSubscription: Subscription;
  private user: User;
  private ONE_DAY = 1000 * 60 * 60 * 24; /* 1 day in ms */
  private isProcessing: boolean;


  constructor(
    private store: Store<fromRoot.State>,
    private skuService: SkuService,
    private chargebeeService:ChargebeeService,
    private userService: UserService,
    private multiadminService: MultiAdminService,
    private blendService: BlendService
  ) {
  }

  public async identify() {
    if (!isUrlHistoryClean()) {
      return;
    }
    this.userSubscribe = this.store
    .pipe(select(fromRoot.getAuthState))
    .subscribe(async (data) => {
      if (data.authenticated) {
        if (this.isProcessing) {
          return;
        }
        this.isProcessing = true;
        let trial_ends;
        let days_to_renewal;
        let renewal_date;
        let uid;
        this.user = data.user;
        if (this.user) {
          if (this.userSubscribe) { this.userSubscribe.unsubscribe(); }
          if (this.user.is_disableanalytics == 1) {
            // todo: if we want to enable tours for users who has disabled analytics
            // chameleon.identify(this.user.id);
            return;
          }

          chameleon.init('SxWyyKmLy9x1GLatcHr4iUUq3mbB4U6x9Ag6O1k00sZgDM-1TdLI1-FyoqEr7JgeAHXMQN', { fastUrl: 'https://fast.chameleon.io/' });

          let plan_name = this.user.plan_name;
          let cprofile;
          if (this.userService.checkPermission(PermissionActions.VIEW_BILLING)) {
            cprofile = await new Promise((resolve) => {
              this.chargebeeSubscription = this.chargebeeService.getSubscription().subscribe(data => {
                if (data.isLoaded) {
                  if (this.chargebeeSubscription) { this.chargebeeSubscription.unsubscribe(); }
                  resolve(data.cprofile);
                }
              });
            });
          } else {
            const cprofileData = await this.chargebeeService.getChargebeeProfile();
            cprofile = cprofileData?.cprofile;
          }

          if (cprofile.subscription) {
            renewal_date = new Date(cprofile.subscription.subscriptionEndDate);
            days_to_renewal = this.getRemainingDays(cprofile.subscription.subscriptionEndDate)
            if(this.user.is_on_trial) {
              trial_ends = this.userService.dateConversionForTrials(cprofile.subscription.subscriptionEndDate, this.user.is_on_trial)
            }
              plan_name = cprofile.subscription.planName;
          }

          if (!uid) {
            uid = this.user.id;
          }

          const role = await this.getRole(this.user.id);
          const sku = await this.skuService.getSku();
          const plan_type = await this.skuService.getPlanType(sku);
          const created = new Date(this.user.join_date);
          const chameleonObj = {
            created,
            company: {
              ...(renewal_date !== undefined ? {renewal_date} : {}),
              ...(uid !== undefined ? {uid} : {}),
              ...(trial_ends !== undefined ? {trial_ends} : {}),
              ...(days_to_renewal !== undefined ? {days_to_renewal} : {}),
              ...(role !== undefined ? {role} : {}),
              sku,
              plan_type,
              plan_name
            }
          }
          chameleon.identify(this.user.id, chameleonObj);
        }
      }
    });
  }

  private getRemainingDays(dateStr: string) {
    if (!dateStr || isNaN(new Date(dateStr).getTime())) {
      return;
    }
    const futureDate = new Date(dateStr);
    const today = new Date();
    if (futureDate <= today) {
      return 0;
    }
    const timeDifference = futureDate.getTime() - today.getTime();
    const daysDifference = timeDifference / this.ONE_DAY;
    return Math.round(daysDifference); // Round to the nearest day
  }


  private async getRole(userId: number) {
    if (this.userService.get('is_multi_new')) {
      const response = await this.multiadminService.getRoles();
      const roles = (response as Role[])['roles'] as Role[];
      for (let role of roles) {
        // Check if the role contains a user with the given user_id
        if (role.users.some(user => user.user_id === userId)) {
          return role.name; // Return the role name if found
        }
      }
    }
    return;
  }

}
